import React, { useState, useEffect } from "react";
import countriesData from "../data/countries.json";
import LoadingButton from "./LoadingButton";
import api from "../services/api";
import { useTranslation } from "react-i18next";
import { CreditCard } from "lucide-react";
import { motion } from "framer-motion";

export default function BillingForm({
  open,
  onClose,
  planLabel,
  planPrice,
  billingCycle,
  onProceedToCheckout,
  initialData = {},
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || "es";
  const [isCompany, setIsCompany] = useState(true);
  const [companyName, setCompanyName] = useState("");
  const [vatId, setVatId] = useState("");
  const [billingContact, setBillingContact] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [stateProv, setStateProv] = useState("");
  const [country, setCountry] = useState("ES");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Default country to ES, but override with initialData if provided
    setCountry((prev) => prev || initialData?.billing_address?.country || "ES");
    // Reset and/or prefill form whenever opened
    if (open) {
      setError(null);
      setIsCompany(initialData?.is_company ?? true);
      setCompanyName(initialData?.company_name || "");
      setVatId(initialData?.vat_id || "");
      setBillingContact(initialData?.billing_contact || "");
      setBillingEmail(initialData?.billing_email || "");
      const addr = initialData?.billing_address || initialData?.address || {};
      setAddress1(addr.address1 || addr.line1 || "");
      setAddress2(addr.address2 || addr.line2 || "");
      setPostalCode(addr.postal_code || addr.postalCode || "");
      setCity(addr.city || "");
      setStateProv(addr.state || addr.stateProv || "");
      setCountry(addr.country || initialData?.country || "ES");
    }
  }, [open, initialData]);

  if (!open) return null;

  const handleConfirm = async () => {
    setError(null);
    if (!billingEmail || !billingContact) {
      setError(
        t("pricing.billingErrorRequired", "Completa los campos obligatorios")
      );
      return;
    }

    setSaving(true);
    try {
      // Save billing details to company config via API (backend should persist)
      await api.saveCompanyBilling({
        company_name: companyName,
        vat_id: vatId,
        billing_contact: billingContact,
        billing_email: billingEmail,
        address: {
          address1,
          address2,
          postal_code: postalCode,
          city,
          state: stateProv,
          country,
        },
        is_company: isCompany,
      });

      // Proceed to checkout: user of onProceedToCheckout will create session
      await onProceedToCheckout({});
    } catch (err) {
      console.error("Billing save error", err);
      setError(
        err?.response?.data?.message ||
          t(
            "pricing.billingSaveError",
            "Error al guardar los datos de facturación"
          )
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black opacity-40"
        onClick={onClose}
      ></div>
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.18 }}
        role="dialog"
        aria-modal="true"
        className="relative bg-white/80 backdrop-blur-xl border border-white/60 shadow-lg rounded-2xl max-w-lg sm:max-w-2xl w-full p-6 z-10 mx-4 sm:mx-0 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center shadow-sm">
                <CreditCard className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-semibold">
                {t("pricing.upgradeTitle", {
                  plan: planLabel,
                  price: planPrice,
                  cycle: billingCycle,
                })}
              </h3>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {t(
                "pricing.billingUpdateSubtitle",
                "Update billing details. Updated billing details will appear on your next invoice."
              )}
            </p>
          </div>
          <button
            className="text-gray-500"
            onClick={onClose}
            aria-label={t("common.close", "Close")}
          >
            ×
          </button>
        </div>

        <div className="space-y-4 mb-4">
          <div className="flex gap-4">
            <button
              onClick={() => setIsCompany(true)}
              className={`flex-1 p-3 rounded-xl border-2 font-bold text-sm transition-all ${
                isCompany
                  ? "bg-primary-50 border-primary-600 text-primary-700 shadow-sm"
                  : "bg-white border-gray-100 text-gray-400 hover:border-gray-200"
              }`}
            >
              {t("pricing.youAreCompany", "You are\na company")}
            </button>
            <button
              onClick={() => setIsCompany(false)}
              className={`flex-1 p-3 rounded-xl border-2 font-bold text-sm transition-all ${
                !isCompany
                  ? "bg-primary-50 border-primary-600 text-primary-700 shadow-sm"
                  : "bg-white border-gray-100 text-gray-400 hover:border-gray-200"
              }`}
            >
              {t("pricing.youAreIndividual", "an individual")}
            </button>
          </div>

          {isCompany && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <div className="text-xs text-gray-600 mb-1">
                  {t("pricing.companyName", "Company name")}
                </div>
                <input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-primary-200 transition"
                />
              </label>
              <label className="block">
                <div className="text-xs text-gray-600 mb-1">
                  {t("pricing.vat", "GST/VAT ID (optional)")}
                </div>
                <input
                  value={vatId}
                  onChange={(e) => setVatId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-primary-200 transition"
                />
              </label>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs text-gray-600 mb-1">
                {t("pricing.billingContact", "Billing contact")}
              </div>
              <input
                value={billingContact}
                onChange={(e) => setBillingContact(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-primary-200 transition"
                placeholder={t(
                  "pricing.billingContactPlaceholder",
                  "Full name or contact"
                )}
              />
            </label>
            <label className="block">
              <div className="text-xs text-gray-600 mb-1">
                {t("pricing.billingEmail", "Billing email")}
              </div>
              <input
                value={billingEmail}
                onChange={(e) => setBillingEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-primary-200 transition"
                placeholder={t(
                  "pricing.billingEmailPlaceholder",
                  "billing@company.com"
                )}
              />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs text-gray-600 mb-1">
                {t("pricing.billingAddress", "Billing address")}
              </div>
              <input
                value={address1}
                onChange={(e) => setAddress1(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-primary-200 transition"
                placeholder={t("pricing.addressPlaceholder", "Street, number")}
              />
            </label>
            <label className="block">
              <div className="text-xs text-gray-600 mb-1">
                {t("pricing.address2", "Address 2 (optional)")}
              </div>
              <input
                value={address2}
                onChange={(e) => setAddress2(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-primary-200 transition"
                placeholder={t(
                  "pricing.address2Placeholder",
                  "Apartment, suite, etc. (optional)"
                )}
              />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs text-gray-600 mb-1">
                {t("pricing.postalCode", "Postal code")}
              </div>
              <input
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-primary-200 transition"
                inputMode="numeric"
              />
            </label>
            <label className="block">
              <div className="text-xs text-gray-600 mb-1">
                {t("pricing.city", "City")}
              </div>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-primary-200 transition"
              />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block">
              <div className="text-xs text-gray-600 mb-1">
                {t("pricing.stateProv", "State/Province (optional)")}
              </div>
              <input
                value={stateProv}
                onChange={(e) => setStateProv(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-primary-200 transition"
              />
            </label>
            <label className="block md:col-span-2">
              <div className="text-xs text-gray-600 mb-1">
                {t("pricing.country", "Country")}
              </div>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-primary-200 transition"
              >
                {countriesData.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {error && <div className="mb-3 text-red-600 text-sm">{error}</div>}

        <div className="bg-gray-50 p-4 rounded-md mb-4 text-sm text-gray-700">
          <p>
            {t("pricing.billingSummary", {
              price: planPrice,
              every: t(
                billingCycle === "yearly" ? "pricing.every_yearly" : "pricing.every_monthly"
              ),
            })}
          </p>
          <p className="mt-2">{t("pricing.billingCarryOver")}</p>
          <p className="mt-2 text-xs text-gray-500">
            {t("pricing.billingEffective", {
              date: new Date().toLocaleDateString(language, {
                year: "numeric",
                month: "long",
                day: "numeric",
              }),
            })}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full border border-gray-200 text-sm bg-white hover:shadow-sm"
          >
            {t("common.cancel", "Cancel")}
          </button>
          <LoadingButton
            loading={saving}
            onClick={handleConfirm}
            className="px-5 py-2.5 rounded-full bg-primary-600 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition"
          >
            {t("pricing.confirm", "Confirm")}
          </LoadingButton>
        </div>
      </motion.div>
    </div>
  );
}
